import { ApolloClient, InMemoryCache } from '@apollo/client/core';
import type { NormalizedCacheObject } from '@apollo/client/core';
import { WebSocketLink } from '@apollo/client/link/ws';
import { onError } from '@apollo/client/link/error';
import { from } from '@apollo/client/link/core';

export default ApolloClient;

function apolloClientFactory(): () => ApolloClient<NormalizedCacheObject> {
  let client: ApolloClient<NormalizedCacheObject> | undefined;

  return () => {
    if (!client) {
      try {
        const wsLink = new WebSocketLink({
          uri: 'wss://smart-search.sembo.com/graphql',
          options: {
            reconnect: true,
            connectionCallback: (error): void => {
              if (error) {
                throw new Error(
                  `[GraphQL Error WebSocket Connection]: ${error}`
                );
              }
            },
          },
        });

        const errorLink = onError(({ graphQLErrors, networkError }) => {
          if (graphQLErrors) {
            graphQLErrors.forEach(({ message, locations, path }) => {
              throw new Error(
                `[GraphQL Error]: ${message}, Location: ${locations}, Path: ${path}`
              );
            });
          }

          if (networkError) {
            console.error(
              `[GraphQL Error Network Error]: ${networkError.message}`
            );

            if (networkError.message.includes('WebSocket')) {
              throw new Error(
                '[GraphQL Error]: WebSocket error detected. Retrying...'
              );
            }
          }
        });

        const link = from([errorLink, wsLink]);

        client = new ApolloClient({
          link,
          cache: new InMemoryCache({
            typePolicies: {
              ItineraryResponseQL: {
                keyFields: ['itineraryKey', 'prices'],
              },
              TimeSlotsQL: {
                keyFields: ['timeSlotId', 'startTime'],
              },
              AgeRangeQL: {
                keyFields: ['ageRangeId', 'price', 'quantityAvailable'],
              },
              SegmentQL: {
                keyFields: [
                  'flightNumber',
                  'departureDateTime',
                  'arrivalDateTime',
                ],
              },
            },
          }),
          connectToDevTools: true,
        });
      } catch (error) {
        throw new Error('[GraphQL Error]: Failed to initialize Apollo Client.');
      }
    }
    return client;
  };
}

export const getApolloClient = apolloClientFactory();
